import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api, { API, resolveImageUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Crop, Save, RotateCcw, Eraser, Lasso, Undo2 } from "lucide-react";

const baseBtn = "inline-flex items-center justify-center gap-1 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] border cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

const TOOLS = [
  { key: "crop", label: "Crop", icon: Crop },
  { key: "eraser", label: "Eraser", icon: Eraser },
  { key: "lasso", label: "Lasso", icon: Lasso },
];

/**
 * Three-tool manual editor for a canvas item:
 *  - Crop: drag a rectangle to trim the image to.
 *  - Eraser: paint to make pixels transparent (PNG output).
 *  - Lasso: click points to enclose a polygon, keep only what's inside.
 *
 * Saves the result either:
 *   - just to the local canvas (no community contribution)
 *   - OR as a `product_variant` so other curators see this as an option
 *
 * Image-loading: when the source image is on a remote host without CORS, the
 * <img crossOrigin="anonymous"> request fails silently and the modal looks
 * "blank". We therefore proxy remote images through /api/images/proxy which
 * fetches them server-side and serves them same-origin.
 */
export default function ManualEditModal({ open, item, productId, onClose, onApply }) {
  const [tool, setTool] = useState("crop");
  const [imgEl, setImgEl] = useState(null);
  const [imgLoadError, setImgLoadError] = useState(false);
  const [box, setBox] = useState(null); // crop: {x,y,w,h} in image-natural coords
  const [drag, setDrag] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [brushSize, setBrushSize] = useState(40); // eraser radius (display px)
  const [strokes, setStrokes] = useState([]); // eraser strokes; each: {points:[{x,y}], r}
  const [poly, setPoly] = useState([]); // lasso polygon, image-natural coords
  const [polyClosed, setPolyClosed] = useState(false);
  const [paintingId, setPaintingId] = useState(null); // current eraser stroke index
  const wrapRef = useRef(null);
  const imgRef = useRef(null);

  // Build a same-origin image source. Any remote URL is routed through the
  // backend proxy so the canvas can read its pixels without a tainted-canvas
  // SecurityError. Relative /api/files/... already stays same-origin.
  const rawUrl = item?.image_url || "";
  const looksRemote = /^https?:\/\//i.test(rawUrl);
  const proxiedUrl = looksRemote
    ? `${API}/images/proxy?url=${encodeURIComponent(rawUrl)}`
    : resolveImageUrl(rawUrl);

  // Reset every time a new item is opened
  useEffect(() => {
    if (!open) return;
    setTool("crop");
    setBox(null);
    setNote("");
    setStrokes([]);
    setPoly([]);
    setPolyClosed(false);
    setImgLoadError(false);
    setImgEl(null);
  }, [open, item?.id]);

  // Initialise crop box to centre 80% once image loads
  useEffect(() => {
    if (!imgEl) return;
    const w = imgEl.naturalWidth, h = imgEl.naturalHeight;
    setBox({ x: Math.round(w * 0.1), y: Math.round(h * 0.1), w: Math.round(w * 0.8), h: Math.round(h * 0.8) });
  }, [imgEl]);

  const naturalSize = imgEl ? { w: imgEl.naturalWidth, h: imgEl.naturalHeight } : { w: 0, h: 0 };
  const wrap = wrapRef.current;
  const wrapRect = wrap?.getBoundingClientRect();
  const scale = wrapRect && naturalSize.w ? wrapRect.width / naturalSize.w : 1;

  // Crop drag handlers (ALWAYS registered so hooks order is stable)
  useEffect(() => {
    if (!drag || !box || tool !== "crop") return;
    const onMove = (e) => {
      const dx = (e.clientX - drag.sx) / scale;
      const dy = (e.clientY - drag.sy) / scale;
      setBox(b => {
        let nb = { ...drag.sb };
        if (drag.mode === "move") {
          nb.x = Math.max(0, Math.min(naturalSize.w - nb.w, drag.sb.x + dx));
          nb.y = Math.max(0, Math.min(naturalSize.h - nb.h, drag.sb.y + dy));
        } else if (drag.mode === "br") {
          nb.w = Math.max(20, Math.min(naturalSize.w - nb.x, drag.sb.w + dx));
          nb.h = Math.max(20, Math.min(naturalSize.h - nb.y, drag.sb.h + dy));
        } else if (drag.mode === "tl") {
          const newX = Math.max(0, drag.sb.x + dx);
          const newY = Math.max(0, drag.sb.y + dy);
          nb.w = Math.max(20, drag.sb.w - (newX - drag.sb.x));
          nb.h = Math.max(20, drag.sb.h - (newY - drag.sb.y));
          nb.x = newX; nb.y = newY;
        }
        return nb;
      });
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, scale, naturalSize.w, naturalSize.h, box, tool]);

  // Eraser drag handlers
  useEffect(() => {
    if (paintingId === null || tool !== "eraser") return;
    const onMove = (e) => {
      if (!wrapRect) return;
      const px = (e.clientX - wrapRect.left) / scale;
      const py = (e.clientY - wrapRect.top) / scale;
      setStrokes(s => {
        const next = s.slice();
        const cur = { ...next[paintingId] };
        cur.points = [...cur.points, { x: px, y: py }];
        next[paintingId] = cur;
        return next;
      });
    };
    const onUp = () => setPaintingId(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [paintingId, scale, wrapRect, tool]);

  if (!open || !item) return null;

  // ---------- Crop interactions ----------
  const onMouseDown = (e, mode) => {
    e.preventDefault(); e.stopPropagation();
    setDrag({ mode, sx: e.clientX, sy: e.clientY, sb: { ...box } });
  };

  // ---------- Eraser interactions ----------
  const startStroke = (e) => {
    if (!wrapRect) return;
    e.preventDefault(); e.stopPropagation();
    const px = (e.clientX - wrapRect.left) / scale;
    const py = (e.clientY - wrapRect.top) / scale;
    setStrokes(s => {
      const next = [...s, { points: [{ x: px, y: py }], r: brushSize / scale }];
      setPaintingId(next.length - 1);
      return next;
    });
  };

  // ---------- Lasso interactions ----------
  const addLassoPoint = (e) => {
    if (polyClosed || !wrapRect) return;
    e.preventDefault(); e.stopPropagation();
    const px = (e.clientX - wrapRect.left) / scale;
    const py = (e.clientY - wrapRect.top) / scale;
    setPoly(p => [...p, { x: px, y: py }]);
  };
  const closeLasso = () => { if (poly.length >= 3) setPolyClosed(true); };
  const undoLasso = () => { setPolyClosed(false); setPoly(p => p.slice(0, -1)); };

  // ---------- Reset ----------
  const reset = () => {
    if (tool === "crop" && imgEl) {
      const w = imgEl.naturalWidth, h = imgEl.naturalHeight;
      setBox({ x: Math.round(w * 0.1), y: Math.round(h * 0.1), w: Math.round(w * 0.8), h: Math.round(h * 0.8) });
    } else if (tool === "eraser") {
      setStrokes([]); setPaintingId(null);
    } else if (tool === "lasso") {
      setPoly([]); setPolyClosed(false);
    }
  };

  // ---------- Undo (per-tool shallow undo) ----------
  const canUndo = (tool === "eraser" && strokes.length > 0)
    || (tool === "lasso" && (poly.length > 0 || polyClosed));
  const undo = () => {
    if (tool === "eraser") {
      setStrokes(s => s.slice(0, -1));
    } else if (tool === "lasso") {
      if (polyClosed) {
        setPolyClosed(false);
      } else {
        setPoly(p => p.slice(0, -1));
      }
    }
  };

  // ---------- Render output ----------
  const renderBlob = async () => {
    if (!imgEl) return null;
    if (tool === "crop") {
      if (!box) return null;
      const c = document.createElement("canvas");
      c.width = box.w; c.height = box.h;
      const ctx = c.getContext("2d");
      ctx.drawImage(imgEl, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
      return await new Promise((res) => c.toBlob(res, "image/png", 0.95));
    }

    if (tool === "eraser") {
      const c = document.createElement("canvas");
      c.width = naturalSize.w; c.height = naturalSize.h;
      const ctx = c.getContext("2d");
      ctx.drawImage(imgEl, 0, 0);
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      for (const s of strokes) {
        if (!s.points.length) continue;
        ctx.lineWidth = Math.max(2, s.r * 2);
        ctx.beginPath();
        ctx.moveTo(s.points[0].x, s.points[0].y);
        for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
        ctx.stroke();
      }
      ctx.globalCompositeOperation = "source-over";
      return await new Promise((res) => c.toBlob(res, "image/png", 0.95));
    }

    if (tool === "lasso") {
      if (poly.length < 3) return null;
      const c = document.createElement("canvas");
      c.width = naturalSize.w; c.height = naturalSize.h;
      const ctx = c.getContext("2d");
      // Build a clipping path of the polygon, draw the image inside it
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(imgEl, 0, 0);
      ctx.restore();
      // Crop to polygon bbox so the canvas item isn't padded with empty space
      const xs = poly.map(p => p.x), ys = poly.map(p => p.y);
      const minX = Math.max(0, Math.floor(Math.min(...xs)));
      const minY = Math.max(0, Math.floor(Math.min(...ys)));
      const maxX = Math.min(naturalSize.w, Math.ceil(Math.max(...xs)));
      const maxY = Math.min(naturalSize.h, Math.ceil(Math.max(...ys)));
      const w = maxX - minX, h = maxY - minY;
      if (w < 4 || h < 4) return null;
      const out = document.createElement("canvas");
      out.width = w; out.height = h;
      out.getContext("2d").drawImage(c, minX, minY, w, h, 0, 0, w, h);
      return await new Promise((res) => out.toBlob(res, "image/png", 0.95));
    }
    return null;
  };

  const applyToCanvas = async () => {
    setBusy(true);
    try {
      const blob = await renderBlob();
      if (!blob) throw new Error("Nothing to save yet");
      const fd = new FormData();
      fd.append("file", blob, `${tool}.png`);
      const { data } = await api.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onApply?.({ image_url: data.image_url, asVariant: false });
      onClose?.();
      toast.success("Applied to canvas");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveAsVariant = async () => {
    if (!productId) { toast.error("This item isn't linked to a product, so it can't be shared as a variant."); return; }
    setBusy(true);
    try {
      const blob = await renderBlob();
      if (!blob) throw new Error("Nothing to save yet");
      const fd = new FormData();
      fd.append("file", blob, `${tool}.png`);
      const { data: up } = await api.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const kind = tool === "crop" ? "crop" : "edit";
      await api.post(`/products/${productId}/variants`, {
        product_id: productId,
        image_url: up.image_url,
        kind,
        note,
      });
      onApply?.({ image_url: up.image_url, asVariant: true });
      onClose?.();
      toast.success("Saved & shared with the collective");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // ---------- Hint per tool ----------
  const hint = tool === "crop"
    ? "Crop · drag the corners to trim."
    : tool === "eraser"
      ? "Eraser · click & drag to paint pixels transparent. Adjust the brush below."
      : "Lasso · click to add polygon points. ‘Close’ when ready.";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="rounded-none border-[#E5E5E5] max-w-4xl max-h-[92vh] p-0 flex flex-col"
        data-testid="manual-edit-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Crop className="w-5 h-5 text-[#9CA47E]" /> Manual edit
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#9CA47E] border border-[#9CA47E] px-1.5 py-[1px] ml-2">Beta</span>
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable body — keeps the DialogFooter buttons pinned at the
            bottom of the modal even on short laptop screens. */}
        <div className="flex-1 overflow-y-auto px-6 pb-3 min-h-0">

        {/* Tool tabs */}
        <div className="flex border border-[#E5E5E5] divide-x divide-[#E5E5E5]" data-testid="manual-edit-tools">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const active = tool === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`tool-${t.key}`}
                onClick={() => setTool(t.key)}
                className={`flex-1 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] cursor-pointer transition-colors inline-flex items-center justify-center gap-1.5 ${active ? "bg-[#333333] text-white" : "bg-white text-[#333333] hover:bg-[#F2EDE3]"}`}
              >
                <Icon className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#595959] mt-2">{hint}</div>

        {/* Eraser brush controls */}
        {tool === "eraser" && (
          <div className="flex items-center gap-3 mt-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#595959]">Brush</span>
            <input
              type="range"
              min="6"
              max="120"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              data-testid="eraser-brush-size"
              className="flex-1 accent-[#9CA47E]"
            />
            <span className="font-mono text-[10px] text-[#333333] w-8 text-right">{brushSize}</span>
            <button type="button" data-testid="eraser-clear" onClick={() => setStrokes([])} className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`}>
              <Undo2 className="w-3 h-3" /> Clear strokes
            </button>
          </div>
        )}

        {/* Lasso controls */}
        {tool === "lasso" && (
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#595959]">Points</span>
            <span className="font-mono text-[10px] text-[#333333]">{poly.length}</span>
            <span className="ml-3 font-mono text-[9px] uppercase tracking-[0.22em] text-[#595959]">{polyClosed ? "Closed" : "Open"}</span>
            <div className="ml-auto flex gap-2">
              <button type="button" data-testid="lasso-undo" onClick={undoLasso} disabled={poly.length === 0} className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`}>
                <Undo2 className="w-3 h-3" /> Undo point
              </button>
              <button type="button" data-testid="lasso-close" onClick={closeLasso} disabled={poly.length < 3 || polyClosed} className={`${baseBtn} border-[#333333] text-[#333333] hover:bg-[#333333] hover:text-white`}>
                Close path
              </button>
            </div>
          </div>
        )}

        <div
          ref={wrapRef}
          className="relative bg-[#F2EDE3] border border-[#E5E5E5] mt-2 mx-auto select-none"
          style={{ maxWidth: 720, cursor: tool === "lasso" ? "crosshair" : tool === "eraser" ? "cell" : "default" }}
          onMouseDown={tool === "eraser" ? startStroke : undefined}
          onClick={tool === "lasso" ? addLassoPoint : undefined}
        >
          <img
            ref={imgRef}
            src={proxiedUrl}
            alt=""
            crossOrigin="anonymous"
            onLoad={(e) => { setImgEl(e.currentTarget); setImgLoadError(false); }}
            onError={() => setImgLoadError(true)}
            className="block w-full h-auto select-none"
            draggable={false}
            style={{ pointerEvents: "none" }}
          />

          {/* Image-load error fallback so the modal doesn't look blank. */}
          {imgLoadError && (
            <div data-testid="manual-edit-load-error" className="absolute inset-0 flex items-center justify-center bg-[#F2EDE3] text-center px-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA47E]">◇ Couldn't load this image</div>
                <p className="font-body text-sm text-[#595959] mt-2 max-w-sm">
                  The remote image refused our request. Try Removing BG first (which re-hosts it on VoraStyle), then re-open Manual Edit.
                </p>
              </div>
            </div>
          )}

          {/* Crop overlay */}
          {tool === "crop" && box && imgEl && (
            <div
              data-testid="crop-box"
              onMouseDown={(e) => onMouseDown(e, "move")}
              className="absolute border-2 border-[#9CA47E] cursor-move"
              style={{
                left: box.x * scale, top: box.y * scale,
                width: box.w * scale, height: box.h * scale,
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
              }}
            >
              <div data-testid="crop-handle-tl" onMouseDown={(e) => onMouseDown(e, "tl")} className="absolute -left-1.5 -top-1.5 w-3 h-3 bg-[#9CA47E] cursor-nwse-resize" />
              <div data-testid="crop-handle-br" onMouseDown={(e) => onMouseDown(e, "br")} className="absolute -right-1.5 -bottom-1.5 w-3 h-3 bg-[#9CA47E] cursor-nwse-resize" />
            </div>
          )}

          {/* Eraser stroke preview */}
          {tool === "eraser" && imgEl && (
            <svg
              data-testid="eraser-overlay"
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${naturalSize.w} ${naturalSize.h}`}
              preserveAspectRatio="none"
            >
              {strokes.map((s, i) => {
                if (s.points.length < 1) return null;
                const d = s.points.reduce(
                  (acc, p, idx) => acc + (idx === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`),
                  ""
                );
                return (
                  <path
                    key={i}
                    d={d}
                    stroke="rgba(224,60,49,0.55)"
                    strokeWidth={s.r * 2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                );
              })}
            </svg>
          )}

          {/* Lasso polygon preview */}
          {tool === "lasso" && imgEl && poly.length > 0 && (
            <svg
              data-testid="lasso-overlay"
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${naturalSize.w} ${naturalSize.h}`}
              preserveAspectRatio="none"
            >
              <polygon
                points={poly.map(p => `${p.x},${p.y}`).join(" ")}
                fill={polyClosed ? "rgba(224,60,49,0.18)" : "rgba(224,60,49,0.08)"}
                stroke="#9CA47E"
                strokeWidth={Math.max(1, naturalSize.w / 320)}
                strokeDasharray={polyClosed ? "" : `${naturalSize.w / 80} ${naturalSize.w / 160}`}
              />
              {poly.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={Math.max(2, naturalSize.w / 200)} fill="#fff" stroke="#9CA47E" strokeWidth={Math.max(1, naturalSize.w / 320)} />
              ))}
            </svg>
          )}
        </div>

        <div className="mt-3">
          <Input
            data-testid="manual-edit-note"
            placeholder="Optional note for the collective — e.g. 'tighter crop'"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-none border-[#E5E5E5]"
          />
        </div>

        </div>
        {/* Pinned action row — never scrolls offscreen */}
        <DialogFooter className="px-6 py-4 gap-2 flex-wrap border-t border-[#E5E5E5] bg-white shrink-0">
          <button type="button" data-testid="manual-edit-undo" onClick={undo} disabled={!canUndo} className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`}>
            <Undo2 className="w-3 h-3" /> Undo
          </button>
          <button type="button" data-testid="manual-edit-reset" onClick={reset} className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`}>
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <button type="button" data-testid="manual-edit-cancel" onClick={onClose} className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`}>
            Cancel
          </button>
          <button type="button" data-testid="manual-edit-apply" disabled={busy} onClick={applyToCanvas} className={`${baseBtn} border-[#333333] text-[#333333] hover:bg-[#333333] hover:text-white`}>
            Apply to canvas
          </button>
          <button type="button" data-testid="manual-edit-save-variant" disabled={busy || !productId} onClick={saveAsVariant} className={`${baseBtn} border-[#9CA47E] bg-[#9CA47E] text-white hover:bg-[#7E876A]`}>
            <Save className="w-3 h-3" /> Save & share
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
